import React, { FC, useEffect, useState } from 'react'
import { DatasetRelationshipsTile } from './DatasetRelationshipsTile'
import { useDatasetTables } from '../../hooks/useDatasetTables'
import { createOption, Option } from 'helpers/selects'
import Loader from 'uiKit/loaders/loader'
import { DatasetRelationshipsHeader } from './DatasetRelationshipsHeader'
import { NewDatasetRelation } from './NewDatasetRelation'
import { RelationshipsItem, RelationshipsRequest } from './relationships.types'
import { datasetRelationService } from '../../service/datasetRelation'
import { alertError, alertSuccess } from 'api'

type DatasetRelationshipsProps = {
  datasetId: string
}

export const DatasetRelationships: FC<DatasetRelationshipsProps> = ({ datasetId }) => {
  const [search, setSearch] = useState('')

  const { data: tables = [], loading} = useDatasetTables(datasetId)
  const [relationships, setRelationships] = useState<RelationshipsItem[]>([])
  useEffect(() => {
    datasetRelationService.getRelations(datasetId).then(result => {
      setRelationships(result)
    })
  }, [datasetId])

  const tableOptions: Option[] = tables.map(({ tableName, tableId }) => createOption(tableName, tableId))
  const columns = new Map(tables.map(t => [t.tableId, t.columns]))

  const displayData = search ?
    relationships.filter(({
      relationshipSourceTableId = '',
      sourceColumnName = '',
      relationshipDestinationTableId = '',
      targetColumnName = '',
    }) => {
      const searchValue = search.toLowerCase()
      return (
        relationshipSourceTableId.toLowerCase().includes(searchValue) ||
        sourceColumnName.toLowerCase().includes(searchValue) ||
        relationshipDestinationTableId.toLowerCase().includes(searchValue) ||
        targetColumnName.toLowerCase().includes(searchValue)
      )
    })
    : relationships

  const handleCreation = async (request: RelationshipsRequest) => {
    if (validateAdding(request)) {
      datasetRelationService.postRelation(datasetId, request).then(relation => {
        setRelationships([...relationships, relation])
        alertSuccess('Relation created successfully')
      })
    } else {
      alertError('There is the same relation')
    }
  }

  const handleDelete = async (relationId: string) => {
    return datasetRelationService.deleteRelation(datasetId, relationId).then(() => alertSuccess('Relation deleted successfully'))
  }

  const handleUpdate = async (relationId: string, relationRequest: RelationshipsRequest) => {
    return datasetRelationService.putRelation(datasetId, relationId, relationRequest)
      .then(() => {
        updateRelation(relationId, relationRequest)
        alertSuccess('Relation updated successfully')
      })
  }

  const validateAdding = (request: RelationshipsRequest) : boolean => {
    return !(relationships.find(r => r.relationshipType == request.relationshipType &&
      r.relationshipDestinationTableId == request.relationshipDestinationTableId &&
      r.relationshipSourceTableId == request.relationshipSourceTableId &&
      r.targetColumnName == request.targetColumnName &&
      r.sourceColumnName == request.sourceColumnName &&
      r.filterDirection == request.filterDirection))
  }

  const getTitleById = (tableId: string) => {
    const find = tables.find(t => t.tableId === tableId)
    return find ? find.tableName : 'Unknown'
  }

  const updateRelation = (relationId: string, update: RelationshipsRequest) => {
    setRelationships(prevRelationships =>
      prevRelationships.map(r => {
        if (r.id === relationId) {
          return {
            relationId,
            ...r,
            ...update,
            relationshipSourceTableTitle: getTitleById(update.relationshipSourceTableId),
            relationshipDestinationTableTitle: getTitleById(update.relationshipDestinationTableId)
          }
        }
        return r
      })
    )

  }

  return (
    <div style={{ paddingTop: '1rem' }}>
      <DatasetRelationshipsHeader search={search} onChange={setSearch} count={relationships?.length}>
        <NewDatasetRelation options={tableOptions} onSubmit={handleCreation} columns={columns} />
      </DatasetRelationshipsHeader>

      {!tables.length && loading && (
        <div style={{ margin: '3rem auto' }}>
          <Loader />
        </div>
      )}

      {!loading &&
        displayData.map(relation => (
          <DatasetRelationshipsTile
            key={relation.id}
            source={relation.relationshipSourceTableId}
            relationId={relation.id}
            sourceColumn={relation.sourceColumnName}
            destination={relation.relationshipDestinationTableId}
            targetColumn={relation.targetColumnName}
            type={relation.relationshipType}
            direction={relation.filterDirection}
            highlight={search}
            datasetId={datasetId}
            tableOptions={tableOptions}
            columns={columns}
            sourceTitle={getTitleById(relation.relationshipSourceTableId)}
            destinationTitle={getTitleById(relation.relationshipDestinationTableId)}
            onDelete={() =>
              handleDelete(relation.id).then(() => setRelationships(relationships.filter(r => r.id !== relation.id)))
            }
            onChange={(relationId, relationRequest) => handleUpdate(relationId, relationRequest)}
          />
        ))}
    </div>
  )
}
